"use client";

import { className as classNameHelper } from "@/composites/foundation/utilities/className";
import {
	Button,
	type ButtonProps,
	CartButton,
	Container,
	type DefaultImageProps,
	type DefaultLinkProps,
	Icon,
	Image,
	type ImageProps,
	Link,
	type LinkProps,
	MenuDropdown,
	SearchButton,
	Text
} from "@pamperedchef/design-system";
import React, { useState } from "react";

import * as styles from "./Header.module.scss";
import { MobileMenu, type MobileMenuProps } from "./MobileMenu";
import { Search, type SearchProps } from "./Search";
import { UtilityLinks, type UtilityLinksProps } from "./UtilityLinks";

export interface ContactConsultantInfo {
	consultant: {
		name: string;
	};
}

type NavigationItem<LinkComponentProps = DefaultLinkProps> =
	| { type: "link"; link: LinkProps<LinkComponentProps> }
	| {
			type: "menu";
			label: string;
			items: LinkProps<LinkComponentProps>[];
	  };

export interface HeaderProps<
	LinkComponentProps = DefaultLinkProps,
	ImageComponentProps = DefaultImageProps
> {
	logo: ImageProps<ImageComponentProps>;
	logoLink: LinkProps<LinkComponentProps>;
	utilityLinks: UtilityLinksProps<LinkComponentProps, ImageComponentProps>;
	navItems: NavigationItem<LinkComponentProps>[];
	cart: {
		cta: ButtonProps;
		itemNumber?: number;
	};
	search: SearchProps;
	consultantImage?: ImageProps<ImageComponentProps>;
	contactConsultantInfo?: ContactConsultantInfo | undefined;
	mobileNav: MobileMenuProps<LinkComponentProps, ImageComponentProps>;
	onContactConsultantClick?: () => void;
	onMyShowsClick?: () => void;
}

/**
 * Header component
 */
export function Header<
	LinkComponentProps = DefaultLinkProps,
	ImageComponentProps = DefaultImageProps
>({
	logo,
	logoLink,
	utilityLinks,
	navItems,
	cart,
	search,
	consultantImage,
	contactConsultantInfo,
	mobileNav,
	onContactConsultantClick,
	onMyShowsClick
}: HeaderProps<LinkComponentProps, ImageComponentProps>): JSX.Element {
	const [showMobileSearch, setMobileSearch] = useState(false);

	const [showConsultantCard, setShowConsultantCard] = useState(true);
	const consultantName = contactConsultantInfo?.consultant.name
		? contactConsultantInfo.consultant.name
		: "Consultant Name";

	return (
		<div className={styles["header"]}>
			<Container
				className={styles["header-top"]}
				containerClassName={styles["container-top"]}
			>
				<div className={styles["logo-wrapper"]}>
					<Link<LinkComponentProps>
						{...logoLink}
						id={"site-logo-desktop"}
					>
						<Image<ImageComponentProps>
							objectFit="contain"
							{...logo}
							{...classNameHelper(
								styles["logo"],
								styles["desktop"],
								logo.className
							)}
							alt={logo.alt || ""}
						/>
					</Link>
				</div>
				<UtilityLinks
					{...utilityLinks}
					onMyShowsClick={onMyShowsClick}
				/>
			</Container>
			<Container
				className={styles["header-bottom"]}
				containerClassName={styles["container-bottom"]}
			>
				<nav
					{...classNameHelper(
						styles["navigation"],
						styles["desktop"]
					)}
				>
					{navItems.map((navItem, idx) => {
						if (navItem.type === "menu") {
							const mappedItems = navItem.items.map((item) => {
								const linkItem =
									item as LinkProps<LinkComponentProps> & {
										href?: string;
									};
								return {
									...linkItem,
									label: linkItem.children as
										string | undefined,
									href: linkItem.href
								};
							});
							return (
								<MenuDropdown
									key={idx}
									className={styles["menu-dropdown"]}
									items={
										mappedItems as unknown as React.ComponentProps<
											typeof MenuDropdown
										>["items"]
									}
									icon={{ name: "ChevronDown" }}
									expandedIcon={{ name: "ChevronUp" }}
								>
									{navItem.label}
								</MenuDropdown>
							);
						}

						return (
							<Link<LinkComponentProps>
								key={idx}
								{...navItem.link}
								size="md"
							/>
						);
					})}

					<div className={styles["search-container"]}>
						<Button
							{...cart.cta}
							{...classNameHelper(
								styles["cart-button"],
								cart.cta.className
							)}
							kind="tertiary"
						>
							{/* v3 cart icon — DS v4 "ShoppingCart" uses a
								different Phosphor icon; this preserves the
								original silhouette that the number count is
								designed to sit inside. */}
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0.5 37.3 26"
								role="img"
								aria-hidden="true"
								style={{
									color: "var(--icon-color, currentcolor)",
									width: "var(--icon-width, var(--icon-size, 24px))",
									height: "var(--icon-height, var(--icon-size, 24px))"
								}}
							>
								<path
									fill="currentColor"
									fillRule="evenodd"
									d="M1.417.506C.86.513.143.572.023 1.116Q0 1.214 0 1.34c0 .515.211 1.038.58 1.16.355.12.404.107 3.484.107h3.269L8.63 11.66c1.187 8.296 1.324 9.077 1.625 9.32.275.223.635.266 2.194.266h1.864l-.466.66c-1.578 2.232.16 5.067 2.728 4.453 2.091-.5 2.804-3.003 1.318-4.624l-.31-.338h8.223l-.462.524c-1.219 1.385-.84 3.491.77 4.268 2.783 1.343 5.29-1.96 3.247-4.28l-.452-.512h2.346c2.166 0 2.237-.107 2.478-.228.314-.165.461-1.17.092-1.793a.45.45 0 0 0-.245-.187.9.9 0 0 0-.278-.047H22.837c-9.974 0-10.612-.016-10.682-.263-.041-.145-.12-.55-.174-.902l-.1-.64H22.43c9.83 0 10.568-.017 10.846-.262.365-.322 4.028-13.914 4.028-14.93.048-.61-.835-.925-1.279-.85-.723.12-.723.481-2.561 7.023-.929 3.514-1.72 6.44-1.758 6.502-.088.142-20.25.15-20.25.008-.107-.547-1.242-7.522-1.927-11.519-.3-1.757-1.54-2.77-3.322-2.787a275 275 0 0 0-4.79-.016"
								/>
							</svg>
							{cart.itemNumber ? (
								<Text
									className={styles["cart-item-number"]}
									tag="span"
									styledAs="xxsb"
								>
									{cart.itemNumber}
								</Text>
							) : undefined}
						</Button>
						<Search {...search} />
						{consultantImage && (
							<div className={styles["consultant-wrapper"]}>
								<Image<ImageComponentProps>
									{...consultantImage}
									{...classNameHelper(
										styles["consultant-image"],
										consultantImage.className
									)}
									aspectRatio="1:1"
									borderRadius="full"
									width={65}
									height={65}
									alt={consultantImage.alt || ""}
									onClick={() =>
										setShowConsultantCard(
											!showConsultantCard
										)
									}
									style={{ cursor: "pointer" }}
								/>
								{showConsultantCard && (
									<div className={styles["consultant-card"]}>
										<div
											className={
												styles["consultant-card-header"]
											}
										>
											<Icon
												name="X"
												size={12}
												className={styles["close-icon"]}
												onClick={() =>
													setShowConsultantCard(false)
												}
											/>
										</div>
										<div
											className={
												styles["consultant-card-body"]
											}
										>
											<Text styledAs="xsl" tag="span">
												Consultant
											</Text>
											<Text styledAs="xsr" tag="span">
												{consultantName}
											</Text>
											<Button
												kind="secondary"
												size="sm"
												onPress={() => {
													setShowConsultantCard(
														false
													);
													Promise.resolve().then(
														() => {
															onContactConsultantClick?.(); // Then open modal safely after microtask
														}
													);
												}}
											>
												Contact Me
											</Button>
										</div>
									</div>
								)}
							</div>
						)}
					</div>
				</nav>
				<div
					{...classNameHelper(
						styles["logo-wrapper"],
						styles["mobile"]
					)}
				>
					<Link<LinkComponentProps>
						{...logoLink}
						id={"site-logo-mobile"}
					>
						<Image<ImageComponentProps>
							{...logo}
							{...classNameHelper(
								styles["logo"],
								styles["mobile"],
								logo.className
							)}
							alt={logo.alt || ""}
						/>
					</Link>
				</div>
				<nav
					{...classNameHelper(styles["navigation"], styles["mobile"])}
				>
					<MobileMenu
						{...mobileNav}
						onMyShowsClick={onMyShowsClick}
					/>
					<SearchButton
						className={styles["mobile-button"]}
						size="md"
						onClick={() => setMobileSearch(!showMobileSearch)}
					/>
					<CartButton
						className={styles["mobile-button"]}
						{...cart.cta}
						itemNumber={cart.itemNumber}
					/>
				</nav>
			</Container>
			<div
				{...classNameHelper(styles["mobile-search-container"], {
					[styles["active"]]: showMobileSearch
				})}
			>
				<Search {...search} />
			</div>
		</div>
	);
}
